<template>
  <div v-if="localFlowInfo">
    <div class="flex align-center mb-3">
      <tw-trading-status-icon :statusCode="flowStatus.code" />
      <div class="text-12 text-bold px-2 py-1" :class="labelClassName">
        {{ flowStatus.label }}
      </div>

      <div v-if="isForcedClose" class="text-12 text-bold px-2 py-1 ml-3 label-forcedClose">
        {{ lang === 'ja' ? statusLabels.FORCED_CLOSE_JAPANESE.label : statusLabels.FORCED_CLOSE_ENGLISH.label }}
      </div>

    </div>
    <div
      v-if="!editMode"
      class="tradingFlowName mb-4 text-18 text-bold flex align-center"
    >
      <div class="mr-2 text-gray-600 text-12">{{ $t('Label.Flow Name') }}:</div>
      <div class="copyable">{{ localFlowInfo.tradingManageName }}</div>
    </div>
    <div
      v-if="!editMode"
      class="tradingFlowName mb-4 text-18 text-bold align-center"
    >
      <div class="flex flex-wrap">
        <div class="">
          <span class="mr-2 text-gray-600 text-12">{{ $t('Label.Flow Reference') }}:</span>
          <span v-if="localFlowInfo.flowReference" class="mr-4 copyable">{{ localFlowInfo.flowReference }}</span>
          <span v-else class="mr-4 text-12 text-gray-600 px-1">-</span>
        </div>

        <div class="">
          <span class="mr-2 text-gray-600 text-12">{{ $t('Label.Flow PIC') }}:</span>
          <span v-if="localFlowInfo.flowUserName" class="copyable">{{ localFlowInfo.flowUserName }}</span>
          <span v-else class="text-12 text-gray-600 px-1">-</span>
        </div>
      </div>
    </div>
    <div class="flex align-end">
      <div
        ref="overviewContainer"
        class="overview-container flex flex-wrap gap-8"
        :style="{ maxHeight: expanded ? 'none' : collapsedHeight + 'px' }"
      >
        <div v-for="item in displayFields" :key="item.key">
          <div class="flex align-center">
            <div class="label mr-1 text-gray-600 p-1 text-12">
              {{ item.label }}
            </div>
            <div v-if="localFlowInfo[item.key]" class="text-gray-800 text-12">
              {{ localFlowInfo[item.key] }}
            </div>
            <div v-else class="text-12 text-gray-600 px-1">
              -
            </div>
          </div>
        </div>
      </div>
      <div v-if="showToggle" class="pl-2 pr-4">
        <el-button v-if="!expanded" type="text" size="mini" @click="expanded = !expanded">
          <i class="el-icon-arrow-up mr-2"></i>
          Show more
        </el-button>
        <el-button v-else type="text" size="mini" @click="expanded = !expanded">
          <i class="el-icon-arrow-down mr-2"></i>
          Show less
        </el-button>
      </div>
    </div>
  </div>
</template>

<script>
import _ from 'lodash';
import {
  defineComponent,
  ref,
  computed,
  nextTick,
  onMounted,
} from '@vue/composition-api';
import { TRADING_PROGRESS_STATUS_VARIABLES, FLOW_HIDDEN_FLAG_VARIABLES, FLOW_HIDDEN_FLAG, LANGUAGE_CODE } from 'lib-tw-common';
import tradingListSchema from '@/dictionaries/tradingListSchema.json';
import { formatQuantity, formatCurrency } from '@/utils/searchUtil.js';

import TwTradingStatusIcon from '@/components/atoms/TwTradingStatusIcon';
import { statusLabels } from '@/dictionaries/status.js';

export default defineComponent({
  props: {
    flowInfo: {
      type: Object,
      require: true,
    },
    editMode: {
      type: Boolean,
      default: false,
    },
  },
  components: {
    TwTradingStatusIcon,
  },
  setup(props, { root }) {
    const language = root.$store.state.locale;
    const lang = _.get(LANGUAGE_CODE, `${language}`, 'ja');

    const expanded = ref(false);
    const collapsedHeight = 64;
    const showToggle = ref(false);
    const overviewContainer = ref(null);

    const localFlowInfo = ref(null);

    const flowStatus = computed(() => {
      const code = props.flowInfo.tradingProgressStatusCd;
      const status = _.find(TRADING_PROGRESS_STATUS_VARIABLES, { code });
      return status;
    });

    const forcedClose = computed(() => {
      const code = props.flowInfo.forcedClose;
      const status = _.find(FLOW_HIDDEN_FLAG_VARIABLES, { code });
      return status;
    });

    const isForcedClose = computed(() => {
      const code = props.flowInfo.forcedClose;
      return code === FLOW_HIDDEN_FLAG.HIDDEN;
    });

    const labelClassName = computed(() => {
      return `label-${flowStatus.value.code}`;
    });

    const displayFields = computed(() => {
      const includeKeys = [
        'invoiceNo',
        'contractNo',
        'sellerName',
        'buyerName',
        'contractGroupOfGoods',
        'contractTotalQuantity',
        'contractTotalAmount',
        'blDate',
        'eta',
        'dischargingCurrentEta',
        'deliveryDate',
        'nameOfVessel',
      ];
      return tradingListSchema
        .filter(schema => includeKeys.includes(schema.key))
        .map(schema => ({
          key: schema.key,
          label: root.$t(`List.${schema.label}`),
        }));
    });

    onMounted(() => {
      const { linkageInfo, ...rest } = props.flowInfo;
      localFlowInfo.value = { ...linkageInfo, ...rest };
      // 実際の高さをチェックして制限する必要があるか判定
      nextTick(() => {
        if (overviewContainer.value?.scrollHeight > collapsedHeight) {
          showToggle.value = true;
        }
      });
    });

    return {
      lang,
      statusLabels,
      flowStatus,
      labelClassName,
      localFlowInfo,
      forcedClose,
      isForcedClose,
      FLOW_HIDDEN_FLAG,
      expanded,
      collapsedHeight,
      showToggle,
      overviewContainer,
      displayFields,
    };
  },
});
</script>

<style scoped>
.label {
  border: 1px solid #ededf4;
  border-radius: 2px;
}
.overview-container {
  overflow: hidden;
  transition: max-height 0.3s ease;
}

.label-0 {
  background-color: #ffc555;
  color: #fff;
  border-radius: 4px;
}

.label-1 {
  background-color: #6dd0c2;
  color: #fff;
  border-radius: 4px;
}

.label-2 {
  background-color: #f0f0f0;
  color: #3e3a39;
  border-radius: 4px;
}

.label-forcedClose {
  background-color: #E00001;
  color: #fff;
  border-radius: 4px;
}
</style>
